import React, { useState, useEffect } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { rtoAPI } from '../services/api';
import './Brands.css';

function RTOOffices() {
  const [rtos, setRTOs] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResults, setSyncResults] = useState([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');

  // Create/Edit modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRTO, setEditingRTO] = useState(null);
  const [formData, setFormData] = useState({
    rtoCode: '',
    district: '',
    state: '',
    address: '',
    phone: '',
    website: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch unique states
  const fetchStates = async () => {
    try {
      const response = await rtoAPI.getStates();
      setStates(response.data || []);
    } catch (err) {
      console.error('Failed to fetch states:', err);
    }
  };

  // Fetch districts based on state
  const fetchDistricts = async (state) => {
    try {
      const params = state ? { state } : {};
      const response = await rtoAPI.getDistricts(params);
      setDistricts(response.data || []);
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    }
  };

  const fetchRTOs = async (currentPage, currentLimit, currentSearch, currentState, currentDistrict) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentState) params.state = currentState;
      if (currentDistrict) params.district = currentDistrict;

      const response = await rtoAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setRTOs(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setRTOs(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch RTO offices');
      setRTOs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchDistricts(stateFilter);
  }, [stateFilter]);

  useEffect(() => {
    fetchRTOs(page, limit, search, stateFilter, districtFilter);
  }, [page, limit, search, stateFilter, districtFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this RTO office?')) {
      try {
        await rtoAPI.delete(id);
        fetchRTOs(page, limit, search, stateFilter, districtFilter);
      } catch (err) {
        setError('Failed to delete RTO office');
      }
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingRTO(null);
    setFormData({
      rtoCode: '',
      district: '',
      state: stateFilter || '',
      address: '',
      phone: '',
      website: ''
    });
    setShowFormModal(true);
  };

  // Open edit modal
  const handleEdit = (rto) => {
    setEditingRTO(rto);
    setFormData({
      rtoCode: rto.rtoCode || '',
      district: rto.district || '',
      state: rto.state || '',
      address: rto.address || '',
      phone: rto.phone || '',
      website: rto.website || ''
    });
    setShowFormModal(true);
  };

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit form (create or update)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      if (editingRTO) {
        await rtoAPI.update(editingRTO.id, formData);
      } else {
        await rtoAPI.create(formData);
      }
      setShowFormModal(false);
      fetchRTOs(page, limit, search, stateFilter, districtFilter);
      fetchStates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save RTO office');
    } finally {
      setFormLoading(false);
    }
  };

  // Close form modal
  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingRTO(null);
    setFormData({ rtoCode: '', district: '', state: '', address: '', phone: '', website: '' });
  };

  // Sync with mode: 'smart' (default), 'full'
  const handleSyncAll = async (mode = 'smart') => {
    setShowSyncOptions(false);
    setSyncing(true);
    setShowSyncModal(true);
    setSyncComplete(false);
    setSyncResults([]);

    try {
      const response = await rtoAPI.syncAll(mode);
      const data = response.data;

      if (data.success || data.totalSynced !== undefined) {
        setSyncResults(data.results || []);
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync RTO offices');
    }

    setSyncComplete(true);
    setSyncing(false);
    fetchRTOs(page, limit, search, stateFilter, districtFilter);
    fetchStates();
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStateFilterChange = (e) => {
    setStateFilter(e.target.value);
    setDistrictFilter(''); // Reset district when state changes
    setPage(1);
  };

  const handleDistrictFilterChange = (e) => {
    setDistrictFilter(e.target.value);
    setPage(1);
  };

  const getTotalSynced = () => {
    return syncResults.reduce((sum, r) => sum + (r.count || 0), 0);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Sync Progress Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{syncComplete ? 'Sync Complete' : 'Syncing RTO Offices...'}</h2>
              {syncComplete && <button className="modal-close" onClick={closeSyncModal}>&times;</button>}
            </div>
            <div className="modal-body sync-modal-body">
              {syncing && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p className="sync-status">
                    <strong>Fetching RTO data from API...</strong>
                  </p>
                  <p className="sync-counts">
                    This may take a moment
                  </p>
                  <div className="progress-bar">
                    <div className="progress-fill indeterminate" style={{ width: '30%' }}></div>
                  </div>
                  <p className="progress-percent">Please wait...</p>
                </div>
              )}

              {syncComplete && syncResults.length > 0 && (
                <div className="sync-results">
                  <h3>Total: {getTotalSynced()} RTO offices synced across {syncResults.length} states</h3>
                  <div className="results-grid">
                    {syncResults.filter(r => r.count > 0).map((result, index) => (
                      <div key={index} className="result-item success">
                        <span className="state-name">{result.state}</span>
                        <span className="count">{result.count} RTOs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {syncComplete && syncResults.length === 0 && (
                <div className="sync-results">
                  <h3>Data is already up to date</h3>
                  <p>No new RTO offices were synced.</p>
                </div>
              )}
            </div>
            {syncComplete && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={closeSyncModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRTO ? 'Edit RTO Office' : 'Add RTO Office'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>RTO Code *</label>
                  <input
                    type="text"
                    name="rtoCode"
                    value={formData.rtoCode}
                    onChange={handleFormChange}
                    required
                    placeholder="e.g., DL-1, MH-01"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>District *</label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleFormChange}
                      required
                      placeholder="District name"
                    />
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleFormChange}
                      required
                      placeholder="State name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    rows="2"
                    placeholder="Full address"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleFormChange}
                      placeholder="Website URL"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingRTO ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>RTO Offices</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add RTO
          </button>
          <div className="sync-dropdown">
            <button
              className="btn btn-sync"
              onClick={() => setShowSyncOptions(!showSyncOptions)}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Data'} ▼
            </button>
            {showSyncOptions && (
              <div className="sync-options-menu">
                <button onClick={() => handleSyncAll('smart')} className="sync-option">
                  <strong>Smart Sync</strong>
                  <span>Skip if data count matches</span>
                </button>
                <button onClick={() => handleSyncAll('full')} className="sync-option">
                  <strong>Full Sync</strong>
                  <span>Re-sync all RTO offices</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search by RTO code, district, state..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <select value={stateFilter} onChange={handleStateFilterChange} className="filter-select">
              <option value="">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <select value={districtFilter} onChange={handleDistrictFilterChange} className="filter-select">
              <option value="">All Districts</option>
              {districts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : rtos.length === 0 ? (
          <div className="empty-state">
            <p>No RTO offices found. {search || stateFilter || districtFilter ? 'Try adjusting your filters.' : 'Click "Sync Data" to fetch RTO offices!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>RTO Code</th>
                  <th>District</th>
                  <th>State</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Website</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rtos.map((rto) => (
                  <tr key={rto.id}>
                    <td>{rto.id}</td>
                    <td><strong>{rto.rtoCode}</strong></td>
                    <td>{rto.district}</td>
                    <td>{rto.state}</td>
                    <td className="address-cell">{rto.address || '-'}</td>
                    <td>{rto.phone || '-'}</td>
                    <td>
                      {rto.website ? (
                        <a href={rto.website.startsWith('http') ? rto.website : `https://${rto.website}`} target="_blank" rel="noopener noreferrer">
                          Visit
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(rto)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(rto.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .sync-dropdown {
          position: relative;
          display: inline-block;
        }
        .sync-options-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          min-width: 250px;
          z-index: 1000;
          margin-top: 5px;
        }
        .sync-option {
          display: block;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          border-bottom: 1px solid #eee;
        }
        .sync-option:last-child {
          border-bottom: none;
        }
        .sync-option:hover {
          background-color: #f5f5f5;
        }
        .sync-option strong {
          display: block;
          color: #333;
          margin-bottom: 2px;
        }
        .sync-option span {
          display: block;
          font-size: 12px;
          color: #666;
        }
        .sync-progress {
          text-align: center;
          padding: 20px;
        }
        .sync-status {
          margin: 15px 0 5px;
          font-size: 16px;
        }
        .sync-counts {
          color: #666;
          font-size: 14px;
          margin-bottom: 15px;
        }
        .progress-percent {
          margin-top: 8px;
          font-weight: bold;
          color: #007bff;
        }
        .sync-results {
          margin-top: 10px;
        }
        .sync-results h3 {
          margin-bottom: 15px;
          color: #28a745;
          text-align: center;
        }
        .sync-results p {
          text-align: center;
          color: #666;
        }
        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 10px;
          max-height: 300px;
          overflow-y: auto;
        }
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          border-radius: 4px;
          font-size: 14px;
        }
        .result-item.success {
          background-color: #d4edda;
          color: #155724;
        }
        .result-item .state-name {
          font-weight: 500;
        }
        .result-item .count {
          font-size: 12px;
          opacity: 0.8;
        }
        .progress-bar {
          width: 100%;
          height: 10px;
          background-color: #e9ecef;
          border-radius: 5px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background-color: #007bff;
          transition: width 0.3s ease;
        }
        .progress-fill.indeterminate {
          animation: indeterminate 1.5s infinite ease-in-out;
        }
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(200%); }
          100% { transform: translateX(-100%); }
        }
        .address-cell {
          max-width: 250px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        /* Modal Styles */
        .modal {
          background: white;
          border-radius: 12px;
          max-width: 520px;
          width: 95%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          position: relative;
        }
        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          text-align: left;
        }
        .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
        }
        .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .modal-body {
          padding: 24px;
          max-height: calc(90vh - 160px);
          overflow-y: auto;
        }
        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background: #fafafa;
        }
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .btn-secondary:hover {
          background: #e0e0e0;
        }
      `}</style>
    </div>
  );
}

export default RTOOffices;
